import { NavLink } from "react-router-dom"
import { useSelector } from "react-redux"
import { useSession } from "../../auth/session"
import { meSelector } from "../../redux/globalSlice"
import { useOffers } from "../../redux/storeSlice"
import { isPrivilegedUser, UserRole } from "../../api/models"
import { useSignOut } from "../../auth/signOut"
import { MFA_REQUIRED } from "../../auth/mfaPolicy"
import nfa_logo_with_name from "../../assets/nfa_logo_with_name.png"
import Avatar from "../widgets/Avatar"
import { RENT_TO_OWN_ENABLED, REWARD_POINTS_ENABLED } from "../../featureFlags"
import { useOffersPageEnabled } from "../../offersAccess"
import { Icon, IconName } from "../widgets/icons"

// each item's glyph is the vocabulary's shape for that concept
// (widgets/icons.tsx) — the same one its pills and chips use elsewhere
interface NavItem {
  to: string
  label: string
  icon: IconName
}

const navItems: NavItem[] = [
  { to: "/", label: "Dashboard", icon: "home" },
  ...(RENT_TO_OWN_ENABLED ? [{ to: "/plan", label: "Rent-to-Own", icon: "subscription" as const }] : []),
  { to: "/licenses", label: "Licenses", icon: "license" },
  { to: "/installers", label: "Installers", icon: "installer" },
  { to: "/redemptions", label: "License Codes", icon: "code" },
  ...(REWARD_POINTS_ENABLED ? [{ to: "/points", label: "Reward Points", icon: "reward" as const }] : []),
]

// appended per user: withdrawn for customers, kept for system-wide admins
const offersNavItem: NavItem = { to: "/offers", label: "Your Offers", icon: "offers" }

const privilegedNavItems: NavItem[] = [
  { to: "/credits", label: "Credits", icon: "credits" },
  { to: "/store", label: "Store", icon: "store" },
  { to: "/products", label: "Products", icon: "product" },
  { to: "/skus", label: "SKUs", icon: "sku" },
  { to: "/users", label: "Users", icon: "users" },
]

function NavIcon({ name }: { name: IconName }) {
  return <Icon name={name} size={16} className="sidebar-link-icon" />
}

export default function Sidebar() {
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const me = useSelector(meSelector)?.data
  const isPrivilegedRole = me !== undefined && isPrivilegedUser(me)
  // the Organization(s) item adapts to the caller: system admins and
  // multi-org members get the INDEX (plural), single-org members go straight
  // to their org's page (singular), everyone else sees no item
  const memberships = me?.memberships ?? []
  const orgNavItem: NavItem | null = me?.role === UserRole.Admin || memberships.length > 1
    ? { to: "/organizations", label: "Organizations", icon: "organization" }
    : memberships.length === 1
      ? { to: `/organizations/${memberships[0].orgId}`, label: "Organization", icon: "organization" }
      : null
  const mfaSatisfied = !MFA_REQUIRED || me?.mfaEnabled === true
  const { total: offerCount } = useOffers(accessToken)
  const offersEnabled = useOffersPageEnabled()
  const onSignOut = useSignOut()

  return (
    <nav className="sidebar">
      <div className="sidebar-logo">
        <img src={nfa_logo_with_name} alt="NFA" />
      </div>
      <ul className="sidebar-nav">
        {[...navItems, ...(offersEnabled ? [offersNavItem] : [])].map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) => `sidebar-link${isActive ? " sidebar-link-active" : ""}${item.to === "/offers" ? " sidebar-link-offers" : ""}`}
            >
              <NavIcon name={item.icon} />
              <span className="sidebar-link-label">{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
      {isPrivilegedRole && mfaSatisfied && (
        <ul className="sidebar-nav sidebar-separator">
          {[...privilegedNavItems, ...(orgNavItem !== null ? [orgNavItem] : [])].map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) => `sidebar-link${isActive ? " sidebar-link-active" : ""}`}
              >
                <NavIcon name={item.icon} />
                <span className="sidebar-link-label">{item.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      )}
      {isPrivilegedRole && !mfaSatisfied && (
        <ul className="sidebar-nav sidebar-separator">
          <li className="sidebar-mfa-notice">
            <span>Admin tools require two-factor authentication.</span>
            <NavLink
              to="/account"
              className={({ isActive }) => `sidebar-link${isActive ? " sidebar-link-active" : ""}`}
            >
              <NavIcon name="mfa" />
              <span className="sidebar-link-label">Enable MFA</span>
            </NavLink>
          </li>
        </ul>
      )}
      <div className="sidebar-bottom">
        {me ? (
          <NavLink
            to="/account"
            className={({ isActive }) => `sidebar-link sidebar-user${isActive ? " sidebar-link-active" : ""}`}
          >
            <Avatar imageUrl={me.imageUrl} size={32} className="sidebar-avatar" />
            <span className="sidebar-user-name">{me.id}</span>
          </NavLink>
        ) : (
          // profile not loaded yet — plain label so the account page stays reachable
          <NavLink
            to="/account"
            className={({ isActive }) => `sidebar-link${isActive ? " sidebar-link-active" : ""}`}
          >
            Account Settings
          </NavLink>
        )}
        <button className="sidebar-signout" onClick={() => onSignOut()}>
          <NavIcon name="signOut" />
          <span className="sidebar-link-label">Sign Out</span>
        </button>
      </div>
    </nav>
  )
}
